import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useBgColorStore from '../../store/bgColor';
import { weatherLabels } from '../../constants/headerConstants';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import { encodeDateForUrl } from '../../utils/dateUtils';
import operationAPI from '../../api/operationApi';
import ReusableButton from '../common/Button/Button';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import { IWeatherSectionProps } from '../../type/weatherSectionProps';
import { showOrReplaceToast } from '../../utils/utility';
import { CUSTOM_TOAST_ID } from '../../constants/constant';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';
import { useWeatherDetails } from '../../hooks/apiHook/operationDetails';

const WeatherSection: React.FC<IWeatherSectionProps> = ({
  selectedDate,
  ownerId,
  isEditWeather,
  setIsEditWeather,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [weatherData, setWeatherData] = useState<{ [key: string]: string }>({});

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<{ [key: string]: string }>({
    defaultValues: {},
  });
  const { bgColor, updateBgColor } = useBgColorStore();

  const {
    data: weatherResponse,
    isLoading: isWeatherResponseLoading,
    refetch: refetchWeatherResponse,
  } = useWeatherDetails(selectedDate, ownerId);

  useEffect(() => {
    if (weatherResponse) {
      setWeatherData(weatherResponse); // Set into local state
      reset(weatherResponse); // Same as your callback
    }
  }, [weatherResponse, reset]);

  const onSubmit = async (formData: { [key: string]: string }) => {
    try {
      const encodedDate = encodeDateForUrl(selectedDate);
      const payload = {
        operationDate: encodedDate,
        ownerId: ownerId,
        wind1: formData?.wind1 ?? '',
        wind2: formData?.wind2 ?? '',
        wind3: formData?.wind3 ?? '',
        wind4: formData?.wind4 ?? '',
        pressure: formData?.pressure ?? '',
        swsd: formData?.swsd ?? '',
      };
      setIsLoading(true);
      const response = await operationAPI.updateWeatherDetails(payload);
      toast.success('Weather details updated successfully!');
      setIsEditWeather(false);
      refetchWeatherResponse();
    } catch (error) {
      console.error(error);
      showOrReplaceToast(
        CUSTOM_TOAST_ID.operation,
        'Failed to update weather details.',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset(weatherData); // Reset form values to original
    setIsEditWeather(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h2 className="font-semibold text-lg mb-2 text-left text-text-secondary mt-2">
        {translate('operation.weatherLabel.winds')}
      </h2>
      <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-4 mb-4">
        {weatherLabels.map(({ key, label }) => (
          <div key={key} className="flex flex-col items-start">
            <label className="text-sm font-medium mb-1 text-textColorGray">
              {label}
            </label>
            <input
              type="text"
              {...register(key)}
              placeholder="0"
              className={`w-20 sm:w-24 h-10 px-2 py-1 border border-customBorder-border rounded-md text-text-customGrey bg-backgroundColor-darkThemeColor`}
              readOnly={!isEditWeather}
            />
          </div>
        ))}
      </div>

      {isEditWeather && (
        <div className="flex w-full justify-end">
          <ReusableButton
            variant="secondary"
            children={'Cancel'}
            icon={
              <div>
                <div className="block dark:hidden">
                  <CrossIcon />
                </div>
                <div className="hidden dark:block">
                  <CrossIconDarkForm />
                </div>
              </div>
            }
            className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
            onClick={handleCancel}
            isDisabled={isLoading}
          />
          <ReusableButton
            icon={<CheckIcon />}
            children={'Confirm'}
            variant="primary"
            className="w-auto bg-background-backgroundColor"
            isDisabled={isLoading || !isDirty}
            isLoader={isLoading}
          />
        </div>
      )}
    </form>
  );
};

export default WeatherSection;
